/** governance domain API */
export * from "@/lib/api/api-types";

import { API_PREFIX } from "@/lib/constants/api";
import {
  apiClient,
  admin,
  authz,
  governance,
  governanceAdmin,
  qs,
  getSharedRead,
  sharedReadCacheKey,
  sharedReadParamParts,
  SHARED_AUTHZ_READ_TTL_MS,
  SHARED_DIRECTORY_READ_TTL_MS,
  SHARED_PAGE_READ_TTL_MS,
  SHARED_SETTINGS_READ_TTL_MS,
  clearAdminRuleContractReadCache,
  clearAdminSettingsReadCache,
  clearAiAuditReadCache,
  clearLlmPrivacyReadCache,
  clearPromptTemplateReadCache,
  clearSharedReadCache,
  type SharedReadOptions,
} from "@/lib/api/client-shared";

import type * as ApiTypes from "@/lib/api/api-types";
import type { ApiResponse } from "@/lib/api/types";

export const governanceApiService = {
  getPromptTemplates: () =>
    getSharedRead(
      sharedReadCacheKey("page_read", ["prompt-templates"]),
      SHARED_SETTINGS_READ_TTL_MS,
      () => apiClient.get<ApiTypes.PromptTemplatesListData>(`${governance}/prompt-templates`),
    ),
  getPromptKinds: () =>
    getSharedRead(
      sharedReadCacheKey("page_read", ["prompt-kinds"]),
      SHARED_SETTINGS_READ_TTL_MS,
      () => apiClient.get<ApiTypes.PromptKindsListData>(`${governance}/prompt-kinds`),
    ),
  getPromptKindCatalogDefault: (kindId: string) =>
    getSharedRead(
      sharedReadCacheKey("page_read", ["prompt-kind-catalog-default", kindId]),
      SHARED_SETTINGS_READ_TTL_MS,
      () =>
        apiClient.get<ApiTypes.PromptKindCatalogDefault>(
          `${governance}/prompt-templates/kinds/${encodeURIComponent(kindId)}/default`,
        ),
    ),
  getPromptTemplateBuiltinDefaults: () =>
    getSharedRead(
      sharedReadCacheKey("page_read", ["prompt-template-builtin-defaults"]),
      SHARED_SETTINGS_READ_TTL_MS,
      () => apiClient.get<ApiTypes.PromptTemplateBuiltinDefaultsData>(`${governance}/prompt-templates/builtin-defaults`),
    ),
  postPromptTemplateUser: async (body: Record<string, unknown>) => {
    const response = await apiClient.post<Record<string, unknown>>(`${governance}/prompt-templates`, body);
    if (response.success) clearPromptTemplateReadCache();
    return response;
  },
  patchPromptTemplate: async (id: string, body: Record<string, unknown>) => {
    const response = await apiClient.patch<Record<string, unknown>>(`${governance}/prompt-templates/${encodeURIComponent(id)}`, body);
    if (response.success) clearPromptTemplateReadCache();
    return response;
  },
  deletePromptTemplate: async (id: string) => {
    const response = await apiClient.delete<unknown>(`${governance}/prompt-templates/${encodeURIComponent(id)}`);
    if (response.success) clearPromptTemplateReadCache();
    return response;
  },
  postPromptTemplateClone: async (body: Record<string, unknown>) => {
    const response = await apiClient.post<Record<string, unknown>>(`${governance}/prompt-templates/clone`, body);
    if (response.success) clearPromptTemplateReadCache();
    return response;
  },
  /** Gateway IAM SSOT — current user resource_items (permissions + service catalog). */
  getGatewayResourceItems: (options?: SharedReadOptions & { scope?: "nav" }) =>
    getSharedRead(
      sharedReadCacheKey("authz", ["resource-items", ...sharedReadParamParts({ scope: options?.scope })]),
      SHARED_AUTHZ_READ_TTL_MS,
      () =>
        apiClient.get<ApiTypes.GovernanceAuthzPolicySnapshotPayload>(
          `${API_PREFIX}/current/profile/resource_items${qs({ scope: options?.scope })}`,
        ),
      options,
    ),
  /** Route contract derived from Gateway service_items (reuses resource_items shared TTL). */
  getGatewayServiceRouteContract: async (options?: SharedReadOptions) => {
    const response = await governanceApiService.getGatewayResourceItems(options);
    if (!response.success) {
      return response as ApiResponse<ApiTypes.GovernanceAuthzRouteContractPayload>;
    }
    const serviceItems = Array.isArray(response.data?.service_items)
      ? response.data.service_items
      : [];
    const routes = serviceItems
      .filter((item): item is Record<string, unknown> => !!item && typeof item === "object")
      .filter((item) => typeof item.path === "string" && typeof item.method === "string")
      .map((item) => ({
        method: String(item.method).toUpperCase(),
        path: String(item.path),
        guards: Array.isArray(item.guards)
          ? item.guards.filter((guard): guard is string => typeof guard === "string")
          : [],
        required_permissions: Array.isArray(item.required_permissions)
          ? item.required_permissions.filter((permission): permission is string => typeof permission === "string")
          : typeof item.permission_key === "string"
            ? [item.permission_key]
            : [],
      }));
    return {
      success: true,
      data: { version: "v1", routes },
      meta: response.meta,
    } satisfies ApiResponse<ApiTypes.GovernanceAuthzRouteContractPayload>;
  },
  getGovernanceUsers: (options?: SharedReadOptions) => {
    const includeTenantMemberships = options?.includeTenantMemberships === true;
    const query = includeTenantMemberships ? "?include_tenant_memberships=true" : "";
    return getSharedRead(
      sharedReadCacheKey("directory", ["admin-users", includeTenantMemberships ? "memberships" : "base"]),
      SHARED_DIRECTORY_READ_TTL_MS,
      () => apiClient.get<ApiTypes.GovernanceUsersPayload>(`${authz}/users${query}`),
      options,
    );
  },
  getGovernanceUsersPage: (params: {
    limit: number;
    offset: number;
    includeTenantMemberships?: boolean;
    q?: string;
    status?: "active" | "disabled";
    role?: string;
    role_tenant_id?: string;
  }) => {
    const includeTenantMemberships = params.includeTenantMemberships === true;
    return apiClient.get<{ items: ApiTypes.GovernanceUser[]; total_count: number }>(
      `${authz}/users${qs({
        limit: params.limit,
        offset: params.offset,
        include_tenant_memberships: includeTenantMemberships ? true : undefined,
        q: params.q,
        status: params.status,
        role: params.role,
        role_tenant_id: params.role_tenant_id,
      })}`,
    );
  },
  getGovernanceAuthzCatalog: () =>
    getSharedRead(
      sharedReadCacheKey("authz", ["admin-authz-catalog"]),
      SHARED_PAGE_READ_TTL_MS,
      () => apiClient.get<ApiTypes.GovernanceAuthzCatalogPayload>(`${authz}/catalog`),
    ),
  getGovernanceAuthzTenants: () =>
    getSharedRead(
      sharedReadCacheKey("authz", ["admin-authz-tenants"]),
      SHARED_PAGE_READ_TTL_MS,
      () => apiClient.get<ApiTypes.GovernanceAuthzTenantsPayload>(`${authz}/tenants`),
    ),
  getGovernanceAuthzTenantsPage: (params: { limit: number; offset: number }) =>
    apiClient.get<{ items: ApiTypes.GovernanceAuthzTenant[]; total_count: number }>(
      `${authz}/tenants${qs({ limit: params.limit, offset: params.offset })}`,
    ),
  putGovernanceAuthzTenant: async (tenantId: string, body: Partial<ApiTypes.GovernanceAuthzTenant>) => {
    const response = await apiClient.put<ApiTypes.GovernanceAuthzTenantPayload>(`${authz}/tenants/${encodeURIComponent(tenantId)}`, body);
    if (response.success) clearSharedReadCache("authz");
    return response;
  },
  deleteGovernanceAuthzTenant: async (tenantId: string) => {
    const response = await apiClient.delete<ApiTypes.GovernanceAuthzTenantDeleteResult>(
      `${authz}/tenants/${encodeURIComponent(tenantId)}`,
    );
    if (response.success) clearSharedReadCache("authz");
    return response;
  },
  getGovernanceAuthzTenantEffectiveAccess: (tenantId: string) =>
    apiClient.get<ApiTypes.GovernanceAuthzTenantEffectiveAccessPayload>(`${authz}/tenants/${encodeURIComponent(tenantId)}/effective-access`),
  getGovernanceAuthzTenantUsers: (
    tenantId: string,
    options?: { includeProfiles?: boolean },
  ) => {
    const includeProfiles = options?.includeProfiles === true;
    const query = includeProfiles ? "?include_profiles=true" : "";
    return apiClient.get<ApiTypes.GovernanceAuthzTenantUsersPayload>(
      `${authz}/tenants/${encodeURIComponent(tenantId)}/users${query}`,
    );
  },
  getGovernanceAuthzPackages: () =>
    getSharedRead(
      sharedReadCacheKey("authz", ["admin-authz-packages"]),
      SHARED_PAGE_READ_TTL_MS,
      () => apiClient.get<ApiTypes.GovernanceAuthzPackagesPayload>(`${authz}/packages`),
    ),
  getGovernanceAuthzTenantCapabilityGroups: (tenantId: string) =>
    apiClient.get<ApiTypes.GovernanceAuthzTenantCapabilityGroupsPayload>(
      `${authz}/tenants/${encodeURIComponent(tenantId)}/capability-groups`,
    ),
  putGovernanceAuthzTenantPackage: async (tenantId: string, packageId: string) => {
    const response = await apiClient.put<ApiTypes.GovernanceAuthzTenantPayload>(
      `${authz}/tenants/${encodeURIComponent(tenantId)}/package`,
      { package_id: packageId },
    );
    if (response.success) clearSharedReadCache("authz");
    return response;
  },
  getGovernanceAuthzTenantRoles: (tenantId: string) =>
    apiClient.get<ApiTypes.GovernanceAuthzTenantRolesPayload>(`${authz}/tenants/${encodeURIComponent(tenantId)}/roles`),
  getGovernanceAuthzTenantRolesPage: (
    tenantId: string,
    params: { limit: number; offset: number; q?: string },
  ) =>
    apiClient.get<{ items: NonNullable<ApiTypes.GovernanceAuthzTenantRolesPayload["roles"]>[number][]; total_count: number }>(
      `${authz}/tenants/${encodeURIComponent(tenantId)}/roles${qs({
        limit: params.limit,
        offset: params.offset,
        q: params.q,
      })}`,
    ),
  getActiveTenant: (options?: SharedReadOptions & { view?: "shell" }) =>
    getSharedRead(
      sharedReadCacheKey("authz", ["active-tenant", ...sharedReadParamParts({ view: options?.view })]),
      SHARED_AUTHZ_READ_TTL_MS,
      () =>
        apiClient.get<ApiTypes.ActiveTenantPayload>(
          `${authz}/active-tenant${qs({ view: options?.view })}`,
        ),
      options,
    ),
  putActiveTenant: async (tenantId: string) => {
    const response = await apiClient.put<ApiTypes.ActiveTenantPayload>(`${authz}/active-tenant`, { tenant_id: tenantId });
    if (response.success) {
      clearSharedReadCache("authz");
      clearSharedReadCache("directory");
    }
    return response;
  },
  getServices: () =>
    getSharedRead(
      sharedReadCacheKey("platform", ["services"]),
      SHARED_PAGE_READ_TTL_MS,
      () => apiClient.get<ApiTypes.PlatformServicesPayload>(`${API_PREFIX}/services`),
    ),
  getGovernanceAuthzServices: () =>
    getSharedRead(
      sharedReadCacheKey("authz", ["admin-authz-services"]),
      SHARED_PAGE_READ_TTL_MS,
      () => apiClient.get<ApiTypes.GovernanceAuthzServicesPayload>(`${authz}/services`),
    ),
  getGovernanceAuthzServiceItems: () =>
    getSharedRead(
      sharedReadCacheKey("authz", ["admin-authz-service-items"]),
      SHARED_PAGE_READ_TTL_MS,
      () => apiClient.get<ApiTypes.GovernanceAuthzServiceItemsPayload>(`${authz}/service-items`),
    ),
  getGovernanceAuthzServiceItemsPage: (params: {
    limit: number;
    offset: number;
    status?: "enabled" | "disabled" | "stale";
    q?: string;
    service_id?: string;
  }) =>
    apiClient.get<{ items: ApiTypes.GovernanceAuthzServiceItemRecord[]; total_count: number }>(
      `${authz}/service-items${qs({
        limit: params.limit,
        offset: params.offset,
        status: params.status,
        q: params.q,
        service_id: params.service_id,
      })}`,
    ),
  getGovernanceAuthzServiceItemsForService: (serviceId: string) =>
    apiClient.get<ApiTypes.GovernanceAuthzServiceItemsPayload>(`${authz}/services/${encodeURIComponent(serviceId)}/items`),
  putGovernanceAuthzService: async (
    serviceId: string,
    body: {
      name?: string;
      route_prefix?: string;
      base_url?: string;
      contract_url?: string;
      enabled?: boolean;
      enforcement_mode?: string;
      metadata?: Record<string, unknown>;
    },
  ) => {
    const response = await apiClient.put<ApiTypes.GovernanceAuthzServicePayload>(`${authz}/services/${encodeURIComponent(serviceId)}`, body);
    if (response.success) clearSharedReadCache("authz");
    return response;
  },
  postGovernanceAuthzServiceSync: async (serviceId: string, body: Record<string, unknown>) => {
    const response = await apiClient.post<ApiTypes.GovernanceAuthzServiceSyncResult>(`${authz}/services/${encodeURIComponent(serviceId)}/sync`, body);
    if (response.success) clearSharedReadCache("authz");
    return response;
  },
  putGovernanceAuthzServiceItem: async (serviceItemId: string, body: { enabled?: boolean; permission_key?: string; metadata?: Record<string, unknown> }) => {
    const response = await apiClient.put<ApiTypes.GovernanceAuthzServiceItemsPayload>(`${authz}/service-items/${encodeURIComponent(serviceItemId)}`, body);
    if (response.success) clearSharedReadCache("authz");
    return response;
  },
  postGovernanceAuthzRole: async (tenantId: string, body: ApiTypes.GovernanceAuthzRoleCreateBody) => {
    const response = await apiClient.post<ApiTypes.GovernanceAuthzRoleWriteResult>(
      `${authz}/tenants/${encodeURIComponent(tenantId)}/roles/${encodeURIComponent(body.role_id)}`,
      body,
    );
    if (response.success) {
      clearSharedReadCache("authz");
      clearSharedReadCache("directory");
    }
    return response;
  },
  deleteGovernanceAuthzRole: async (tenantId: string, roleId: string) => {
    const response = await apiClient.delete<ApiTypes.GovernanceAuthzRoleDeleteResult>(
      `${authz}/tenants/${encodeURIComponent(tenantId)}/roles/${encodeURIComponent(roleId)}`,
    );
    if (response.success) {
      clearSharedReadCache("authz");
      clearSharedReadCache("directory");
    }
    return response;
  },
  getGovernanceAuthzRoleDetail: (tenantId: string, roleId: string) =>
    apiClient.get<ApiTypes.GovernanceAuthzRolesPayload>(
      `${authz}/tenants/${encodeURIComponent(tenantId)}/roles/${encodeURIComponent(roleId)}`,
    ),
  patchGovernanceAuthzRole: async (tenantId: string, roleId: string, body: ApiTypes.GovernanceAuthzRolePatchBody) => {
    const response = await apiClient.patch<ApiTypes.GovernanceAuthzRoleWriteResult>(
      `${authz}/tenants/${encodeURIComponent(tenantId)}/roles/${encodeURIComponent(roleId)}`,
      body,
    );
    if (response.success) {
      clearSharedReadCache("authz");
      clearSharedReadCache("directory");
    }
    return response;
  },
  putGovernanceAuthzRolePermissions: async (tenantId: string, roleId: string, body: { permissions: string[] }) => {
    const response = await apiClient.put<ApiTypes.GovernanceAuthzRolePermissionsUpdateResult>(
      `${authz}/tenants/${encodeURIComponent(tenantId)}/roles/${encodeURIComponent(roleId)}/permissions`,
      body,
    );
    if (response.success) clearSharedReadCache("authz");
    return response;
  },
  getGovernanceAuthzTenantRoleServiceItems: (tenantId: string, roleId: string) =>
    apiClient.get<ApiTypes.GovernanceAuthzRoleServiceItemsPayload>(
      `${authz}/tenants/${encodeURIComponent(tenantId)}/roles/${encodeURIComponent(roleId)}/service-items`,
    ),
  putGovernanceAuthzTenantRoleServiceItems: async (
    tenantId: string,
    roleId: string,
    body: ApiTypes.GovernanceAuthzRoleServiceItemsUpdateBody,
  ) => {
    const response = await apiClient.put<ApiTypes.GovernanceAuthzRoleServiceItemsPayload>(
      `${authz}/tenants/${encodeURIComponent(tenantId)}/roles/${encodeURIComponent(roleId)}/service-items`,
      body,
    );
    if (response.success) clearSharedReadCache("authz");
    return response;
  },
  putGovernanceAuthzTenantRoleCapabilityGroups: async (
    tenantId: string,
    roleId: string,
    body: ApiTypes.GovernanceAuthzRoleCapabilityGroupsUpdateBody,
  ) => {
    const response = await apiClient.put<ApiTypes.GovernanceAuthzRoleServiceItemsPayload>(
      `${authz}/tenants/${encodeURIComponent(tenantId)}/roles/${encodeURIComponent(roleId)}/capability-groups`,
      body,
    );
    if (response.success) clearSharedReadCache("authz");
    return response;
  },
  getGovernanceAuthzTenantPermissionCeiling: (tenantId: string) =>
    apiClient.get<ApiTypes.GovernanceAuthzPermissionCeilingPayload>(
      `${authz}/tenants/${encodeURIComponent(tenantId)}/permission-ceiling`,
    ),
  putGovernanceAuthzTenantPermissionCeiling: async (
    tenantId: string,
    body: ApiTypes.GovernanceAuthzPermissionCeilingUpdateBody,
  ) => {
    const response = await apiClient.put<ApiTypes.GovernanceAuthzPermissionCeilingPayload>(
      `${authz}/tenants/${encodeURIComponent(tenantId)}/permission-ceiling`,
      body,
    );
    if (response.success) clearSharedReadCache("authz");
    return response;
  },
  postGovernanceAuthzUser: async (body: ApiTypes.GovernanceAuthzUserCreateBody) => {
    const response = await apiClient.post<ApiTypes.GovernanceAuthzUserWriteResult>(`${authz}/users`, body);
    if (response.success) {
      clearSharedReadCache("authz");
      clearSharedReadCache("directory");
    }
    return response;
  },
  getGovernanceAuthzUser: (userId: string) =>
    apiClient.get<ApiTypes.GovernanceAuthzUserWriteResult>(`${authz}/users/${encodeURIComponent(userId)}`),
  patchGovernanceAuthzUser: async (userId: string, body: ApiTypes.GovernanceAuthzUserPatchBody) => {
    const response = await apiClient.patch<ApiTypes.GovernanceAuthzUserWriteResult>(`${authz}/users/${encodeURIComponent(userId)}`, body);
    if (response.success) {
      clearSharedReadCache("authz");
      clearSharedReadCache("directory");
    }
    return response;
  },
  putGovernanceAuthzUserPassword: async (userId: string, body: ApiTypes.GovernanceAuthzUserPasswordAdminBody) => {
    const response = await apiClient.put<ApiTypes.GovernanceAuthzUserPasswordAdminResult>(`${authz}/users/${encodeURIComponent(userId)}/password`, body);
    if (response.success) {
      clearSharedReadCache("authz");
      clearSharedReadCache("directory");
    }
    return response;
  },
  deleteGovernanceAuthzUser: async (userId: string) => {
    const response = await apiClient.delete<ApiTypes.GovernanceAuthzUserDeleteResult>(`${authz}/users/${encodeURIComponent(userId)}`);
    if (response.success) {
      clearSharedReadCache("authz");
      clearSharedReadCache("directory");
    }
    return response;
  },
  getGovernanceAuthzTenantUserRoles: (tenantId: string, userId: string) =>
    apiClient.get<ApiTypes.GovernanceAuthzUserRolesPayload>(
      `${authz}/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/roles`,
    ),
  putGovernanceAuthzTenantUserRoles: async (tenantId: string, userId: string, body: { role_ids: string[] }) => {
    const response = await apiClient.put<ApiTypes.GovernanceAuthzUserRolesPayload>(
      `${authz}/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/roles`,
      body,
    );
    if (response.success) clearSharedReadCache("authz");
    return response;
  },
  getGovernanceAuthzScopeCatalog: () =>
    getSharedRead(
      sharedReadCacheKey("authz", ["gateway-authz-scope-catalog"]),
      SHARED_PAGE_READ_TTL_MS,
      () => apiClient.get<ApiTypes.GovernanceAuthzScopeCatalogPayload>(`${authz}/scope-catalog`),
    ),
  getGovernanceAuthzScopePolicy: () =>
    getSharedRead(
      sharedReadCacheKey("authz", ["gateway-authz-scope-policy"]),
      SHARED_PAGE_READ_TTL_MS,
      () => apiClient.get<ApiTypes.GovernanceAuthzScopePolicyPayload>(`${authz}/scope-policy`),
    ),
  putGovernanceAuthzScopePolicy: async (body: { rules: ApiTypes.GovernanceAuthzScopePolicyRule[] }) => {
    const response = await apiClient.put<ApiTypes.GovernanceAuthzScopePolicyPayload>(`${authz}/scope-policy`, body);
    if (response.success) clearSharedReadCache("authz");
    return response;
  },
  postGovernanceAuthzScopePolicySimulate: (body: ApiTypes.GovernanceAuthzScopeSimulateBody) =>
    apiClient.post<ApiTypes.GovernanceAuthzScopeSimulateResult>(`${authz}/scope-policy/simulate`, body),
  getGovernanceAuthzAuditEvents: (params?: Record<string, unknown>) =>
    getSharedRead(
      sharedReadCacheKey("authz", ["gateway-authz-audit-events", ...sharedReadParamParts(params)]),
      SHARED_PAGE_READ_TTL_MS,
      () => apiClient.get<ApiTypes.GovernanceAuthzAuditEventsPayload>(`${authz}/audit-events${qs(params ?? {})}`),
    ),
  getGovernanceAuthzAuditEvent: (eventId: string) =>
    getSharedRead(
      sharedReadCacheKey("authz", ["gateway-authz-audit-event", eventId]),
      SHARED_PAGE_READ_TTL_MS,
      () => apiClient.get<ApiTypes.GovernanceAuthzAuditEventPayload>(`${authz}/audit-events/${encodeURIComponent(eventId)}`),
    ),
  postGovernanceAuthzRoutePolicySimulate: (body: ApiTypes.GovernanceAuthzRoutePolicySimulateBody) =>
    apiClient.post<ApiTypes.GovernanceAuthzRoutePolicySimulateResult>(`${authz}/route-policy/simulate`, body),
  getGovernanceRuleContract: () =>
    getSharedRead(
      sharedReadCacheKey("page_read", ["admin-rule-contract"]),
      SHARED_PAGE_READ_TTL_MS,
      () => apiClient.get<ApiTypes.GovernanceRuleContractPayload>(`${admin}/rules/contract`),
    ),
  postGovernanceRuleContractCheck: () => apiClient.post<ApiTypes.GovernanceSetupCheckResult>(`${admin}/rules/contract/check`, {}),
  patchGovernanceRuleContract: async (body: ApiTypes.GovernanceRuleContractPatchBody) => {
    const response = await apiClient.patch<ApiTypes.GovernanceRuleContractPayload>(`${admin}/rules/contract`, body);
    if (response.success) clearAdminRuleContractReadCache();
    return response;
  },
  patchGovernanceRuleContractSqlRows: async (body: ApiTypes.GovernanceRuleContractSqlRowsPatchBody) => {
    const response = await apiClient.patch<ApiTypes.GovernanceRuleContractPayload>(`${admin}/rules/contract/sql-rows`, body);
    if (response.success) clearAdminRuleContractReadCache();
    return response;
  },
  getGovernanceTemplateRoutingContract: () =>
    getSharedRead(
      sharedReadCacheKey("page_read", ["governance-template-routing-contract"]),
      SHARED_PAGE_READ_TTL_MS,
      () =>
        apiClient.get<{ hub_rule_contract_rows: ApiTypes.GovernanceRuleContractRow[] }>(
          `${governance}/template-routing/contract`,
        ),
    ),
  patchGovernanceTemplateRoutingContractSqlRows: async (body: ApiTypes.GovernanceRuleContractSqlRowsPatchBody) => {
    const response = await apiClient.patch<{ hub_rule_contract_rows: ApiTypes.GovernanceRuleContractRow[] }>(
      `${governance}/template-routing/contract/sql-rows`,
      body,
    );
    if (response.success) {
      clearSharedReadCache("page_read");
      clearAdminRuleContractReadCache();
    }
    return response;
  },
  postGovernanceTemplateRoutingRulesDebug: (body: { sender: string; user_id?: string | null }) =>
    apiClient.post<ApiTypes.GovernanceTemplateRoutingDebugPayload>(`${governance}/template-routing/debug`, body),
  getGovernanceSettingsIntegrations: () =>
    getSharedRead(
      sharedReadCacheKey("page_read", ["admin-settings-integrations"]),
      SHARED_PAGE_READ_TTL_MS,
      () => apiClient.get<ApiTypes.GovernanceIntegrationsPayload>(`${admin}/settings/integrations`),
    ),
  getGovernanceSettingsWeights: async () => {
    const response = await apiClient.get<ApiTypes.GovernanceRuleContractPayload>(`${admin}/rules/contract`);
    if (!response.success) {
      return response as ApiResponse<{ weights: ApiTypes.GovernanceSettingsWeightRule[] }>;
    }
    const envelopes = response.data?.rules?.weight_rules ?? [];
    const weights: ApiTypes.GovernanceSettingsWeightRule[] = envelopes
      .map((envelope) => {
        const payload = envelope.payload && typeof envelope.payload === "object" ? envelope.payload : {};
        const unit_from = typeof payload.unit_from === "string" ? payload.unit_from.trim() : "";
        const unit_to = typeof payload.unit_to === "string" ? payload.unit_to.trim() : "";
        const factorRaw = payload.factor;
        const factor = typeof factorRaw === "number" ? factorRaw : Number(factorRaw);
        if (!unit_from || !unit_to || !Number.isFinite(factor)) {
          return null;
        }
        return { unit_from, unit_to, factor };
      })
      .filter((row): row is ApiTypes.GovernanceSettingsWeightRule => row !== null);
    return { success: true, data: { weights } };
  },
  postGovernanceSettingsIntegrationsCheck: () => apiClient.post<ApiTypes.GovernanceSetupCheckResult>(`${admin}/settings/integrations/check`, {}),
  postGovernanceSettingsIntegrationsCheckMail: (body?: ApiTypes.SettingsMailPayload | Record<string, unknown>) =>
    apiClient.post<ApiTypes.GovernanceSetupCheckResult>(`${admin}/settings/integrations/check/mail`, body ?? {}),
  postGovernanceSettingsIntegrationsCheckNotify: () => apiClient.post<ApiTypes.GovernanceSetupCheckResult>(`${admin}/settings/integrations/check/notify`, {}),
  postGovernanceSettingsIntegrationsCheckSharepoint: () => apiClient.post<ApiTypes.GovernanceSetupCheckResult>(`${admin}/settings/integrations/check/sharepoint`, {}),
  getGovernanceSettingsOperational: (params?: { view?: "sync" | "full" }) =>
    getSharedRead(
      sharedReadCacheKey("page_read", ["admin-settings-operational", ...sharedReadParamParts(params)]),
      SHARED_PAGE_READ_TTL_MS,
      () => apiClient.get<{
        operational?: ApiTypes.GovernanceOperationalSettings;
        operational_effective?: ApiTypes.GovernanceOperationalEffectiveSettings;
        operational_sources?: ApiTypes.GovernanceOperationalSources;
        operational_task_models?: Record<string, ApiTypes.GovernanceOperationalTaskModel>;
        operational_model_validation?: Record<string, ApiTypes.GovernanceOperationalModelValidation>;
        operational_runtime?: ApiTypes.GovernanceOperationalRuntime;
      }>(`${admin}/settings/operational${qs(params ?? {})}`),
    ),
  postGovernanceSettingsOperationalCheck: () => apiClient.post<ApiTypes.GovernanceSetupCheckResult>(`${admin}/settings/operational/check`, {}),
  patchGovernanceSettingsOperational: async (body: Record<string, unknown>) => {
    const response = await apiClient.patch<unknown>(`${admin}/settings/operational`, body);
    if (response.success) clearAdminSettingsReadCache();
    return response;
  },
  patchGovernanceSettingsIntegrations: async (body: ApiTypes.SettingsMailPayload | Record<string, unknown>) => {
    const response = await apiClient.patch<unknown>(`${admin}/settings/integrations`, body);
    if (response.success) clearAdminSettingsReadCache();
    return response;
  },
  postGovernancePromptTemplate: async (body: Record<string, unknown>) => {
    const response = await apiClient.post<Record<string, unknown>>(`${governanceAdmin}/prompt-templates`, body);
    if (response.success) clearPromptTemplateReadCache();
    return response;
  },
  postGovernancePromptTemplateProductDefaultSync: async () => {
    const response = await apiClient.post<Record<string, unknown>>(
      `${governanceAdmin}/prompt-templates/product-default/sync`,
      {}
    );
    if (response.success) clearPromptTemplateReadCache();
    return response;
  },
  patchGovernancePromptTemplate: async (id: string, body: Record<string, unknown>) => {
    const response = await apiClient.patch<Record<string, unknown>>(`${governanceAdmin}/prompt-templates/${encodeURIComponent(id)}`, body);
    if (response.success) clearPromptTemplateReadCache();
    return response;
  },
  deleteGovernancePromptTemplate: async (id: string) => {
    const response = await apiClient.delete<unknown>(`${governanceAdmin}/prompt-templates/${encodeURIComponent(id)}`);
    if (response.success) clearPromptTemplateReadCache();
    return response;
  },
  postGovernanceTriageTemplateDebug: (body: {
    template_id?: string | null;
    template_body?: string | null;
    email_subject?: string | null;
    email_body: string;
    email_sender?: string | null;
    attachment_summary?: string | null;
    attachment_names?: string | null;
  }) => apiClient.post<ApiTypes.GovernanceTriageTemplateDebugPayload>(`${governanceAdmin}/prompt-templates/triage/debug`, body),
  getGovernanceAiAuditSummary: (params?: Record<string, unknown>) =>
    getSharedRead(
      sharedReadCacheKey("page_read", ["admin-ai-audit-summary", ...sharedReadParamParts(params)]),
      SHARED_PAGE_READ_TTL_MS,
      () => apiClient.get<ApiTypes.GovernanceAiAuditSummary>(`${governance}/ai-audit/summary${qs(params ?? {})}`),
    ),
  getGovernanceAiAuditEntries: (params?: Record<string, unknown>) =>
    getSharedRead(
      sharedReadCacheKey("page_read", ["admin-ai-audit-entries", ...sharedReadParamParts(params)]),
      SHARED_PAGE_READ_TTL_MS,
      () => apiClient.get<ApiTypes.GovernanceAiAuditEntriesPayload>(`${governance}/ai-audit${qs(params ?? {})}`),
    ),
  getGovernanceAiAuditDetail: (auditId: string) =>
    getSharedRead(
      sharedReadCacheKey("page_read", ["admin-ai-audit-detail", auditId]),
      SHARED_PAGE_READ_TTL_MS,
      () => apiClient.get<ApiTypes.GovernanceAiAuditDetail>(`${governance}/ai-audit/${encodeURIComponent(auditId)}`),
    ),
  getGovernanceAiAuditRaw: (auditId: string) => apiClient.get<{ entry: ApiTypes.GovernanceAiAuditEntry; raw: ApiTypes.GovernanceAiAuditRaw }>(`${governance}/ai-audit/${encodeURIComponent(auditId)}/raw`),
  postGovernanceAiAuditCleanup: async () => {
    const response = await apiClient.post<unknown>(`${governance}/ai-audit/cleanup`, {});
    if (response.success) clearAiAuditReadCache();
    return response;
  },
  getGovernanceLlmPrivacyRules: () =>
    getSharedRead(
      sharedReadCacheKey("page_read", ["admin-llm-privacy-rules"]),
      SHARED_PAGE_READ_TTL_MS,
      () => apiClient.get<ApiTypes.GovernanceLlmPrivacyRulesPayload>(`${governance}/llm-privacy/rules`),
    ),
  patchGovernanceLlmPrivacyRules: async (body: { name: string; enabled: boolean; rules: ApiTypes.GovernanceRule[] }) => {
    const response = await apiClient.patch<ApiTypes.GovernanceLlmPrivacyRulesPayload>(`${governance}/llm-privacy/rules`, body);
    if (response.success) clearLlmPrivacyReadCache();
    return response;
  },
  postGovernanceLlmPrivacyRulesValidate: async () => {
    const response = await apiClient.post<{ draft_ruleset?: ApiTypes.GovernanceRuleset | null; validation_summary?: ApiTypes.GovernanceLlmPrivacyValidationSummary }>(`${governance}/llm-privacy/rules/validate`, {});
    if (response.success) clearLlmPrivacyReadCache();
    return response;
  },
  postGovernanceLlmPrivacyRulesPublish: async (body: { ruleset_id?: string | null }) => {
    const response = await apiClient.post<ApiTypes.GovernanceLlmPrivacyRulesPayload>(`${governance}/llm-privacy/rules/publish`, body);
    if (response.success) clearLlmPrivacyReadCache();
    return response;
  },
  postGovernanceLlmPrivacyRulesImport: async (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    const response = await apiClient.post<ApiTypes.GovernanceLlmPrivacyRulesPayload>(`${governance}/llm-privacy/rules/import`, fd);
    if (response.success) clearLlmPrivacyReadCache();
    return response;
  },
  getGovernanceLlmPrivacyRulesExport: (scope: "draft" | "published", format: "csv" | "xlsx") =>
    apiClient.getFile(`${governance}/llm-privacy/rules/export${qs({ scope, format })}`),
  getGovernanceLlmPrivacyVerify: () => apiClient.get<ApiTypes.GovernanceLlmPrivacyVerifyPayload>(`${governance}/llm-privacy/verify`),
};

/** Prefetch active-tenant shell once per session (stale-while-revalidate via shared read cache).
 * Nav-scope resource_items is large (~100KB+); load on IAM/nav expand only — not during list TTFI. */
export function warmAuthzBootstrapCache(): void {
  void governanceApiService.getActiveTenant({ view: "shell" });
}

let activeTenantWarmPayload: ApiTypes.ActiveTenantPayload | null = null;

/** Apply active-tenant payload from session bootstrap for TenantProvider first paint. */
export function applyActiveTenantWarmPayload(payload: ApiTypes.ActiveTenantPayload): void {
  activeTenantWarmPayload = payload;
}

export function consumeActiveTenantWarmPayload(): ApiTypes.ActiveTenantPayload | null {
  const payload = activeTenantWarmPayload;
  activeTenantWarmPayload = null;
  return payload;
}

export const apiService = governanceApiService;
